import { useLayoutEffect, useRef, useState } from 'react';
import { IoSend } from 'react-icons/io5';

import botImage from '../../assets/chat/chat bot.png';
import Button from '../ui/button';
import { BRAND_COLORS } from '../utils/constants';
import { ChatStatus } from '../utils/enums';
import EndChatButton from './EndChatButton';
import IsTyping from './IsTyping';
import { Chats } from './types/chatbot-types';
import { formatIntroMessage } from './utils/initial-messages';
import { Role } from './utils/roles-enum';

import { ChatBotSessionResponseDto } from '@/interfaces/api/chat/ChatBotSessionResponseDto';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

interface Props {
	chatProp: ChatBotSessionResponseDto;
	title: string;
	theme?: string;
	avatar?: string;
	logo?: string;
	redirectToLocalCheckout?: boolean;
	productId?: string;
	storeName?: string;
	isCSVHaggle?: boolean;
	vendorEmail?: string;
}

export default function Chat({
	chatProp,
	theme,
	avatar,
	redirectToLocalCheckout,
	productId,
	storeName,
	isCSVHaggle,
	vendorEmail,
}: Readonly<Props>) {
	const [message, setMessage] = useState('');
	const [chats, setChats] = useState<Chats[]>([
		{
			role: Role.ASSISTANT,
			content: formatIntroMessage(chatProp.intro_message),
		},
	]);
	const [isTyping, setIsTyping] = useState(false);
	const [hasSentInitialOffer, setHasSentInitialOffer] = useState(false);
	const [isOfferAccepted, setIsOfferAccepted] = useState<string>();
	const [acceptedOffer, setAcceptedOffer] = useState<number | undefined>(1);
	const [units, setUnits] = useState(1);
	const [discountUrl, setDiscountUrl] = useState<string>();
	const [productDiscount, setProductDiscount] = useState<any>(null);
	const isTypingRef = useRef<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		isTypingRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [chats, isTyping]);

	const chat = async (e: React.FormEvent, message: string) => {
		e.preventDefault();

		if (!message) return;

		if (!hasSentInitialOffer) setHasSentInitialOffer(true);
		setIsTyping(true);

		const msgs = chats;
		msgs.push({ role: Role.USER, content: message });
		setChats(msgs);

		setMessage('');

		try {
			const response = isCSVHaggle
				? await haggleApiService.sendLocalMessage(message, chatProp.session_id)
				: await haggleApiService.sendMessage(message, chatProp.session_id);

			msgs.push({
				role: Role.ASSISTANT,
				content: response.message,
			});

			setIsOfferAccepted(response.lastOfferStatus);
			if (response.lastOfferStatus === ChatStatus.Accepted) {
				setAcceptedOffer(response.lastAssistantOfferPerUnit.pricePerUnit);
				setUnits(response.lastAssistantOfferPerUnit.units);
				setDiscountUrl(response.discountUrl);

				if (response.discount) {
					setProductDiscount(response.discount);
				}
			}

			setChats(msgs);
			setIsTyping(false);
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to send message: ${error}`,
				);
			}
		} finally {
			setIsTyping(false);
		}
	};

	return (
		<>
			{!chatProp ? (
				<></>
			) : (
				<main className="w-full h-full flex flex-col">
					<section className="flex flex-col justify-between h-full overflow-y-auto overscroll-contain">
						<div className="p-2 flex-grow flex flex-col gap-3">
							{chats.length
								? chats.map((chat, index) => (
										<div className="flex gap-2" key={`${chat.role}-${index}`}>
											<div>
												{chat.role === 'user' ? (
													<div className="rounded-full p-1 mt-2">
														<div className="w-[30px]"></div>
													</div>
												) : (
													<div className="rounded-full p-1 mt-2">
														<img
															src={avatar || botImage}
															alt="Chatbot Avatar"
															className="w-[30px]"
														/>
													</div>
												)}
											</div>
											<p
												className={
													(chat.role === 'user'
														? 'mr-0 ml-auto'
														: `${theme ? '' : 'bg-slate-100'}`) +
													' p-4 max-w-[70%] w-fit rounded-3xl  break-words'
												}
												style={
													chat.role === 'user'
														? { background: BRAND_COLORS.PURPLE_500 }
														: theme
														? { background: theme }
														: {}
												}
												data-testid={`${index}-message`}
											>
												<span
													className={`m-[5px] ${
														chat.role === 'user' ? 'text-white' : 'text-black'
													}`}
													style={{ whiteSpace: 'pre-wrap' }}
													dangerouslySetInnerHTML={{
														__html: chat.content,
													}}
												></span>
											</p>
										</div>
								  ))
								: ''}
						</div>
						<div ref={isTypingRef} className="flex w-full justify-center">
							<IsTyping isTyping={isTyping} />
						</div>
					</section>
					<footer
						className="flex-shrink-0 sticky bottom-0"
						style={{ background: theme || 'rgb(214,247,216)' }}
					>
						{chatProp.isOutOfStock ? (
							<div className="w-full text-center">
								<Button
									className="p-8 w-full text-black"
									disabled={true}
									style={{ backgroundColor: '#ccc' }}
									shape="none"
									transition="none"
								>
									<p className="text-center text-black">
										This product is currently out of stock
									</p>
								</Button>
							</div>
						) : isOfferAccepted === ChatStatus.Accepted &&
						  acceptedOffer &&
						  discountUrl ? (
							<EndChatButton
								discount={productDiscount}
								finalPrice={acceptedOffer}
								quantity={units}
								discountUrl={discountUrl}
								discountDuration={chatProp.limitCheckoutTime}
								redirectToLocalCheckout={redirectToLocalCheckout}
								data-testid="end-chat-button"
								productId={productId}
								storeName={storeName}
								isCSVHaggle={isCSVHaggle}
								vendorEmail={vendorEmail}
							/>
						) : isOfferAccepted === ChatStatus.Rejected ? (
							<div className="w-full text-center">
								<Button
									className="p-8 w-full text-black"
									disabled={true}
									style={{ backgroundColor: '#ccc' }}
									shape="none"
									transition="none"
								>
									<p className="text-center text-black">
										Offer rejected, please start haggle again!
									</p>
								</Button>
							</div>
						) : (
							<div className="w-full border-t">
								<form action="" onSubmit={(e) => chat(e, message)}>
									<div className="flex justify-between items-center gap-2 w-full px-[10px] py-1 relative">
										<input
											type="text"
											name="message"
											value={message}
											placeholder="Enter your message..."
											className="w-full p-[10px] outline-none focus:ring-0 rounded-full"
											onChange={(e) => setMessage(e.target.value)}
											data-test="chat-input"
										/>
										<Button
											type="submit"
											className="my-1 p-4"
											style={{ background: theme || 'rgb(148,204,164)' }}
											shape="circle"
											transition="none"
											data-test="submit-button"
											isLoading={isTyping}
											disabled={isTyping}
										>
											<IoSend style={{ color: 'white' }} className="text-lg" />
										</Button>
									</div>
								</form>
							</div>
						)}
					</footer>
				</main>
			)}
		</>
	);
}
